import { useEffect, useMemo, useRef, useState } from 'react';

import Button from '@liquidweb-dev/undercurrent/Button';
import CircleUserIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleUser';
import CopyButton from '@liquidweb-dev/undercurrent/CopyButton';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import ShieldIcon from '@liquidweb-dev/undercurrent/icons/lucide/Shield';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import HookedSubmit from 'components/Form/HookedSubmit';
import HookedTextField from 'components/Form/HookedTextField';
import useDebounce from 'utility/effects/useDebounce';
import useAccountUserAvailable from 'modules/queries/account/user/useAvailable';
import useAccountChatPasswordGet from 'modules/queries/account/chatPassword/useGet';
import { roleDisplay, SELECTABLE_ROLES } from 'modules/users';
import LWForm, { FormSubmitHandler } from 'components/LWForm';
import HookedTextFieldPassword from 'components/Form/HookedTextFieldPassword';
import InputAdornment from '@liquidweb-dev/undercurrent/InputAdornment';
import { useIsSelf } from 'modules/oidc/useAuthenticated';

export interface UserFormValues {
	email: string;
	firstname: string;
	lastname: string;
	role: string;
	username: string;
	password: string;
	confirmPassword: string;
	passphrase: string;
}

interface UserFormProps {
	onClose: () => void;
	email?: string;
	firstname?: string;
	lastname?: string;
	role?: string;
	roles?: string[];
	username?: string;
	onSubmit: FormSubmitHandler<UserFormValues>;
	isSubmitting?: boolean;
	isEditing?: boolean;
	/** if true, the username and role fields will be disabled */
	restricted?: boolean;
	/** if true, the passphrase input will be focused */
	focusPassphrase?: boolean;
}

const UserForm = ({
	onClose,
	email = '',
	firstname = '',
	lastname = '',
	role = '',
	roles,
	username = '',
	onSubmit,
	isSubmitting,
	isEditing = false,
	restricted,
	focusPassphrase,
}: UserFormProps) => {
	const [formValues, setFormValues] = useState<UserFormValues>({
		email,
		firstname,
		lastname,
		role,
		username,
		password: '',
		confirmPassword: '',
		passphrase: '',
	});
	const [formErrors, setFormErrors] = useState({});

	const isSelf = useIsSelf(username);

	const isAccountOwner = roles?.includes('AccountOwner');

	const { data: chatPasswordData } = useAccountChatPasswordGet();

	const debouncedUsername = useDebounce(formValues.username, 500);
	const { data: usernameAvailableData, isLoading: isUsernameAvailableLoading } =
		useAccountUserAvailable(
			{ username: debouncedUsername },
			{
				enabled: Boolean(debouncedUsername) && debouncedUsername !== '',
			},
		);

	useEffect(() => {
		if (isSelf && chatPasswordData && formValues?.passphrase === '') {
			setFormValues((values) => ({
				...values,
				passphrase: chatPasswordData.password || '',
			}));
		}
	}, [chatPasswordData, formValues?.passphrase, isSelf]);

	const passphraseRef = useRef<HTMLInputElement>(null);

	// focus the passphrase input if the prop is true
	useEffect(() => {
		if (focusPassphrase) {
			passphraseRef.current?.focus();
		}
	}, [focusPassphrase]);

	const isUsernameAvailable = useMemo(() => {
		if (isUsernameAvailableLoading || formValues.username === username) {
			return true;
		}

		return usernameAvailableData?.available === 1;
	}, [
		formValues.username,
		username,
		usernameAvailableData,
		isUsernameAvailableLoading,
	]);

	const isUserModified = useMemo(
		() =>
			formValues.username !== username ||
			formValues.email !== email ||
			formValues.firstname !== firstname ||
			formValues.lastname !== lastname ||
			formValues.role !== role ||
			formValues.password,
		[formValues, username, email, firstname, lastname, role],
	);

	const isPassphraseModified = useMemo(
		() => isSelf && formValues.passphrase !== chatPasswordData?.password,
		[formValues.passphrase, chatPasswordData?.password, isSelf],
	);

	const isModified = useMemo(
		() => isUserModified || isPassphraseModified,
		[isPassphraseModified, isUserModified],
	);

	const handlePassphraseCopy = () => {
		navigator.clipboard.writeText(formValues.passphrase);
	};

	return (
		<LWForm
			values={formValues}
			onValuesChange={setFormValues}
			errors={formErrors}
			onErrorsChange={setFormErrors}
			onSubmit={onSubmit}
			validators={{
				username: (value: unknown) => {
					if (typeof value !== 'string' || value.length < 2) {
						return 'Username must be at least 2 characters';
					}
					if (value.length > 20) {
						return 'Username must be less than 20 characters';
					}
					return '';
				},
				confirmPassword: (
					value: unknown,
					otherValues: UserFormValues | undefined,
				) => {
					if (typeof value !== 'string' || value !== otherValues?.password) {
						return 'Passwords do not match';
					}
					return '';
				},
			}}
			style={{
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
			}}
		>
			<DrawerContent>
				<Stack spacing={500}>
					<Stack spacing={200}>
						<Stack
							direction="row"
							spacing={50}
							sx={{
								alignItems: 'center',
							}}
						>
							<CircleUserIcon />
							<Typography variant="body-sm">PROFILE</Typography>
						</Stack>
						<Stack spacing={200}>
							<Stack direction="row" spacing={200}>
								<HookedTextField name="firstname" label="First name" required />
								<HookedTextField name="lastname" label="Last name" required />
							</Stack>
							<HookedTextField name="email" label="Email" required />
							<HookedTextField
								name="username"
								label="Username"
								required
								disabled={restricted}
								// @ts-expect-error
								error={
									!isUsernameAvailable ? 'Username is not availabe' : false
								}
							/>
							{isAccountOwner ? (
								<TextField
									select
									label="User role"
									value="AccountOwner"
									disabled
								>
									<MenuItem value="AccountOwner">
										{roleDisplay('AccountOwner')}
									</MenuItem>
								</TextField>
							) : (
								<HookedTextField
									select
									name="role"
									label="User role"
									placeholder="Choose role"
									required
									disabled={restricted}
								>
									{SELECTABLE_ROLES.map((item) => (
										<MenuItem key={item} value={item}>
											{roleDisplay(item)}
										</MenuItem>
									))}
								</HookedTextField>
							)}
						</Stack>
					</Stack>
					<Stack spacing={200}>
						<Stack
							direction="row"
							spacing={50}
							sx={{
								alignItems: 'center',
							}}
						>
							<ShieldIcon />
							<Typography variant="body-sm">SECURITY</Typography>
						</Stack>
						<Stack spacing={200}>
							<HookedTextFieldPassword
								name="password"
								label={isEditing ? 'New password' : 'Password'}
								required={!isEditing}
							/>

							<HookedTextFieldPassword
								name="confirmPassword"
								label="Confirm Password"
								type="password"
								autoComplete="new-password"
								required={!isEditing}
							/>

							{isSelf && (
								<HookedTextFieldPassword
									name="passphrase"
									label="Passphrase"
									slotProps={{
										htmlInput: {
											ref: passphraseRef,
										},
										input: {
											endAdornment: (
												<InputAdornment position="end">
													<CopyButton
														onClick={handlePassphraseCopy}
														iconButtonProps={{ variant: 'barebones' }}
													/>
												</InputAdornment>
											),
										},
									}}
								/>
							)}
						</Stack>
					</Stack>
				</Stack>
			</DrawerContent>
			<DrawerFooter spacing={100}>
				<HookedSubmit
					size="large"
					disabled={
						isUsernameAvailableLoading || !isUsernameAvailable || !isModified
					}
					loading={isSubmitting}
				>
					{isEditing ? 'Save changes' : 'Add user'}
				</HookedSubmit>
				<Button variant="ghost" size="large" onClick={onClose}>
					Cancel
				</Button>
			</DrawerFooter>
		</LWForm>
	);
};

export default UserForm;
